import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import Header from "../../components/Header";
import RailSixSigmaPanel from "../../components/RailSixSigmaPanel";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { ShortfallBanner, WagonPipelineRibbon } from "./shared";
import { Link } from "react-router-dom";
import {
    Panel, statusPill, fmtTime, fmtDt, todayLabel, nowClock,
    IcnMine, IcnYard, IcnCrane, IcnEmpty, IcnTrain, IcnCheck,
} from "./shared";

export default function OverviewDashboard() {
    const [rows, setRows] = useState([]);
    const [overview, setOverview] = useState(null);
    const [notifs, setNotifs] = useState({ items: [] });
    const [autoRefresh, setAutoRefresh] = useState(true);

    const load = useCallback(async () => {
        try {
            const [cs, ov, nt] = await Promise.all([
                api.get("/rail/consignments?include_completed=true"),
                api.get("/rail/overview"),
                api.get("/rail/notifications?limit=30"),
            ]);
            setRows(cs.data || []);
            setOverview(ov.data);
            setNotifs(nt.data);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => {
        load();
        if (!autoRefresh) return;
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [load, autoRefresh]);

    const b = overview?.buckets || {};
    const active = rows.filter((c) => c.status !== "cycle_complete");
    const trainsAtMine = active.filter((c) => ["draft", "loading"].includes(c.status)).length;
    const emptiesReturning = active.filter((c) => c.status === "empty_wagons_returning" ||
                                                    c.status === "empty_wagons_in_transit_to_mine").length;
    const totalTrains = active.length;
    const activeDelays = rows.filter((c) => [c.delay_dispatch_min, c.delay_transnet_arr_min,
        c.delay_transnet_release_min, c.delay_grindrod_arr_min].some((d) => d && d > 15)).length;

    // Today aggregation
    const today = new Date().toISOString().slice(0, 10);
    const tippedToday = rows.reduce((s, c) => s +
        ((c.actual_completion_at || "").startsWith(today) ? (c.wagons_tipped || 0) : 0), 0);
    const tippingNow = rows.reduce((s, c) => s +
        (c.status === "tipping_in_progress" ? ((c.wagons_released || 0) - (c.wagons_tipped || 0)) : 0), 0);
    const totalActiveWagons = active.reduce((s, c) => s + (c.wagon_count || 0), 0);
    const remainingWagons = Math.max(0, totalActiveWagons - tippedToday - tippingNow);

    const tippingSummary = [
        { name: "Tipped",     value: tippedToday, color: "#2563EB" },
        { name: "In Progress", value: tippingNow,  color: "#EA580C" },
        { name: "Remaining",   value: remainingWagons, color: "#94A3B8" },
    ];
    const summaryTotal = tippingSummary.reduce((s, x) => s + x.value, 0) || 1;

    const trainsMoved = rows.filter((c) => (c.last_update_at || "").startsWith(today)).length;
    const wagonsMoved = rows.reduce((s, c) => s +
        ((c.last_update_at || "").startsWith(today) ? (c.wagon_count || 0) : 0), 0);
    const tonsMoved = rows.reduce((s, c) => s +
        ((c.last_update_at || "").startsWith(today) ? Number(c.tonnage || 0) : 0), 0);

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <main className="max-w-[1700px] mx-auto px-3 md:px-6 py-4 space-y-3">
                <div className="rounded-lg text-white p-4 flex items-center justify-between gap-3 flex-wrap"
                     style={{ backgroundColor: "#081A36" }}>
                    <div className="flex items-center gap-3 min-w-0">
                        <IcnTrain color="#93C5FD" size={38} />
                        <div>
                            <h1 className="text-xl md:text-2xl font-black uppercase tracking-wide">Rail Operations Overview</h1>
                            <div className="text-xs text-white/70">Live view of all trains and empty wagons</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="px-3 py-1.5 rounded bg-white/10 text-[11px] font-mono">📅 {todayLabel()}</div>
                        <div className="px-3 py-1.5 rounded bg-white/10 text-[11px] font-mono">🕒 {nowClock()}</div>
                        <button onClick={() => setAutoRefresh((v) => !v)}
                                data-testid="ov-autorefresh"
                                className="px-3 py-1.5 rounded bg-white/10 text-[11px] font-black uppercase tracking-widest">
                            Auto refresh: <span className={autoRefresh ? "text-emerald-300" : "text-red-300"}>{autoRefresh ? "ON ●" : "OFF"}</span>
                        </button>
                    </div>
                </div>

                <ShortfallBanner testid="ov-shortfalls" />

                <div className="flex justify-end">
                    <Link to="/rail-consignments"
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-[var(--grindrod-navy)] hover:bg-[#1a2a55] text-white text-[11px] font-black uppercase tracking-widest transition"
                          data-testid="ov-view-consignments-btn">
                        📋 View All Consignments
                    </Link>
                </div>

                {/* Big pictorial flow */}
                <Panel title={null} testid="ov-flow">
                    <div className="flex items-center gap-2 overflow-x-auto py-3">
                        <FlowNode idx={1} label="MINE"                title="Trains at Mine"       value={trainsAtMine}     colour="#065F46" icon={<IcnMine size={44} />} />
                        <FlowInTransit label="In Transit"              value={b.mine_to_transnet || 0} colour="#16A34A" />
                        <FlowNode idx={2} label="TRANSNET STAGING YARD" title="Trains at Yard"     value={b.at_transnet || 0} colour="#EA580C" icon={<div className="flex items-center"><IcnYard size={44} /><IcnCrane size={38} /></div>} />
                        <FlowInTransit label="In Transit"              value={b.transnet_to_grindrod || 0} colour="#EA580C" />
                        <FlowNode idx={3} label="GRINDROD (TIPPLER)"   title="Trains Tipping"       value={b.at_grindrod || 0} colour="#2563EB" icon={<IcnCrane size={44} />} />
                        <FlowInTransit label="Returning Empty"          value={emptiesReturning} colour="#7C3AED" empty />
                        <FlowNode label="EMPTY WAGONS RETURNING TO MINE" title="Trains Returning"   value={emptiesReturning} colour="#065F46" icon={<IcnMine size={44} />} />
                    </div>
                </Panel>

                {/* 7 KPI row */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    <KpiBig label="Total Trains"   value={totalTrains}         icon={<IcnTrain size={30} color="#334155" />} />
                    <KpiBig label="In Transit"     value={b.mine_to_transnet + b.transnet_to_grindrod || 0} icon={<IcnTrain size={30} color="#166534" />} />
                    <KpiBig label="At Transnet Yard" value={b.at_transnet || 0}       icon={<IcnYard size={30} />} tint="#EA580C" />
                    <KpiBig label="At Grindrod (Tipping)" value={b.at_grindrod || 0}  icon={<IcnCrane size={30} />} tint="#2563EB" />
                    <KpiBig label="Returning Empty" value={emptiesReturning}          icon={<IcnEmpty size={30} />} tint="#7C3AED" />
                    <KpiBig label="Completed (Today)" value={b.completed_today || 0}  icon={<IcnCheck size={30} />} tint="#065F46" />
                    <KpiBig label="Active Delays"   value={activeDelays}              icon={<div className="text-3xl">⚠</div>} tint="#DC2626" />
                </div>

                {/* Live train status + events */}
                <div className="grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-3">
                    <Panel title="Live Train Status" testid="ov-live-status">
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                                    <tr className="text-left border-b">
                                        <th className="pb-1 pr-2">Consignment</th>
                                        <th className="pb-1 pr-2">Train ID</th>
                                        <th className="pb-1 pr-2">Client</th>
                                        <th className="pb-1 pr-2 text-right">Wagons</th>
                                        <th className="pb-1 pr-2 text-right">Tons</th>
                                        <th className="pb-1 pr-2">Current Location</th>
                                        <th className="pb-1 pr-2">Status</th>
                                        <th className="pb-1 pr-2">Last Update</th>
                                        <th className="pb-1 pr-2">Next ETA</th>
                                        <th className="pb-1 pr-2">Tipping Progress</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {active.slice(0, 12).map((c) => (
                                        <tr key={c.id} className="border-b hover:bg-slate-50" data-testid={`ov-row-${c.consignment_no}`}>
                                            <td className="py-1 pr-2 font-mono">{c.consignment_no}</td>
                                            <td className="py-1 pr-2 font-mono">{c.train_id || "—"}</td>
                                            <td className="py-1 pr-2">{c.client_name}</td>
                                            <td className="py-1 pr-2 text-right">{c.wagon_count}</td>
                                            <td className="py-1 pr-2 text-right">{Number(c.tonnage || 0).toLocaleString()}</td>
                                            <td className="py-1 pr-2 text-[11px]">{c.current_location || "—"}</td>
                                            <td className="py-1 pr-2">{statusPill(c.status)}</td>
                                            <td className="py-1 pr-2">{fmtTime(c.last_update_at)}</td>
                                            <td className="py-1 pr-2">{fmtTime(c.eta_grindrod_arrival_at || c.eta_transnet_arrival_at)}</td>
                                            <td className="py-1 pr-2 min-w-[100px]">
                                                {(c.wagons_released || 0) > 0 ? (
                                                    <div className="flex items-center gap-1">
                                                        <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                                            <div className="h-full bg-blue-600"
                                                                 style={{ width: `${pct(c.wagons_tipped, c.wagon_count)}%` }} />
                                                        </div>
                                                        <span className="text-[10px] font-bold">{c.wagons_tipped || 0}/{c.wagon_count}</span>
                                                    </div>
                                                ) : <span className="text-slate-400 text-[10px]">—</span>}
                                            </td>
                                        </tr>
                                    ))}
                                    {active.length === 0 && (
                                        <tr><td colSpan={10} className="py-6 text-center text-slate-500">No active trains.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel title="Today's Events" testid="ov-events">
                        <div className="space-y-1.5 text-[11px] max-h-80 overflow-y-auto">
                            {notifs.items.slice(0, 8).map((n) => (
                                <div key={n.id} className="flex items-start gap-2 border-b last:border-0 pb-1.5">
                                    <span className="text-slate-400 font-mono w-11 flex-shrink-0">{fmtTime(n.created_at)}</span>
                                    <span className="text-slate-800 min-w-0">{n.message}</span>
                                </div>
                            ))}
                            {notifs.items.length === 0 && (
                                <div className="text-slate-500 text-center py-4">No events today.</div>
                            )}
                        </div>
                    </Panel>
                </div>

                {/* Bottom row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <Panel title="Tipping Summary (Today)" testid="ov-tipping-summary">
                        <div className="flex items-center gap-3">
                            <div className="w-28 h-28 flex-shrink-0 relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={tippingSummary} innerRadius={30} outerRadius={50} paddingAngle={2} dataKey="value">
                                            {tippingSummary.map((d, i) => <Cell key={i} fill={d.color} />)}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                    <div className="text-center">
                                        <div className="text-sm font-black">{tippedToday}/{summaryTotal}</div>
                                        <div className="text-[8px] uppercase text-slate-500">Wagons Tipped</div>
                                    </div>
                                </div>
                            </div>
                            <div className="text-xs flex-1 space-y-1">
                                {tippingSummary.map((d) => (
                                    <div key={d.name} className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                                        <span className="flex-1">{d.name}</span>
                                        <b>{d.value} ({Math.round(d.value * 100 / summaryTotal)}%)</b>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Panel>

                    <Panel title="Performance (Today)" testid="ov-performance">
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Avg Turnaround</div>
                                <div className="text-2xl font-black">—</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">On-Time</div>
                                <div className="text-2xl font-black text-emerald-600">
                                    {activeDelays === 0 ? "100%" : `${Math.max(0, 100 - activeDelays * 5)}%`}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Active Delays</div>
                                <div className="text-2xl font-black text-red-600">{activeDelays}</div>
                            </div>
                        </div>
                    </Panel>

                    <Panel title="Key Figures (Today)" testid="ov-keyfigures">
                        <div className="grid grid-cols-3 gap-2 text-center text-xs">
                            <div>
                                <IcnTrain size={22} color="#2563EB" />
                                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Trains Moved</div>
                                <div className="text-2xl font-black">{trainsMoved}</div>
                            </div>
                            <div>
                                <div className="text-2xl">🚃</div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Wagons Moved</div>
                                <div className="text-2xl font-black">{wagonsMoved}</div>
                            </div>
                            <div>
                                <div className="text-2xl">⚖</div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Tons Moved</div>
                                <div className="text-2xl font-black">{Math.round(tonsMoved).toLocaleString()}</div>
                            </div>
                        </div>
                    </Panel>
                </div>

                {/* Live Six-Sigma Analysis + production recommendations */}
                <RailSixSigmaPanel role="overview" />
            </main>
        </div>
    );
}
function pct(a, b) { const t = Number(b || 0); const n = Number(a || 0); return t ? Math.round(n * 100 / t) : 0; }
function KpiBig({ label, value, icon, tint }) {
    return (
        <div className="bg-white rounded-lg border p-3 text-center relative">
            {tint && <div className="absolute inset-x-0 top-0 h-1 rounded-t-lg" style={{ backgroundColor: tint }} />}
            <div className="grid place-items-center h-8">{icon}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mt-1">{label}</div>
            <div className="text-3xl font-black text-slate-900">{value}</div>
        </div>
    );
}
function FlowNode({ idx, label, title, value, colour, icon }) {
    return (
        <div className="min-w-[140px] flex-1 text-center">
            {idx && (
                <div className="text-white text-[10px] font-black w-5 h-5 rounded grid place-items-center mx-auto mb-1"
                     style={{ backgroundColor: colour }}>{idx}</div>
            )}
            <div className="text-[11px] font-black uppercase tracking-widest" style={{ color: colour }}>{label}</div>
            <div className="my-2 grid place-items-center h-14">{icon}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500">{title}</div>
            <div className="text-3xl font-black" style={{ color: colour }}>{value}</div>
        </div>
    );
}
function FlowInTransit({ label, value, colour, empty }) {
    return (
        <div className="min-w-[90px] flex-shrink-0 text-center">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="my-2 flex items-center gap-1">
                {empty ? <IcnEmpty color={colour} size={24} /> : <IcnTrain color={colour} size={24} />}
                <div className="flex-1 h-0.5" style={{ backgroundColor: colour }} />
                <span style={{ color: colour }}>▶</span>
            </div>
            <div className="text-2xl font-black" style={{ color: colour }}>{value}</div>
        </div>
    );
}
